import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAppContext } from '../../../context/AppContext';
import { useWorkspaceContext } from '../providers/WorkspaceProvider';
import { Bot, PenLine, LayoutGrid } from 'lucide-react';
import CenterWorkspace from './CenterWorkspace';
import RightWorkspace from './RightWorkspace';

function CoverLetterSkeleton() {
  return (
    <div 
      className="flex-1 flex h-full min-h-0 overflow-hidden" 
      style={{ background: 'var(--surface-0)', fontFamily: "'Inter', sans-serif" }}
    >
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 0.25; }
        }
        .sk-pulse {
          animation: pulse 1.8s infinite;
          background: var(--divider);
          border-radius: 6px;
        }
      `}</style>
      
      {/* Col 2: Chat panel skeleton */}
      <div className="w-1/2 flex flex-col border-r h-full p-6 space-y-6" style={{ borderColor: 'var(--border-default)' }}>
        <div className="sk-pulse w-32 h-6" />
        <div className="flex-1 space-y-4">
          <div className="sk-pulse w-3/4 h-12 rounded-xl" />
          <div className="sk-pulse w-1/2 h-10 rounded-xl" />
        </div>
        <div className="sk-pulse w-full h-12 rounded-xl" />
      </div>
      
      {/* Col 3: Canvas skeleton */}
      <div className="w-1/2 flex flex-col items-center justify-center p-8 h-full bg-slate-900/10">
        <div className="sk-pulse w-[400px] h-[550px] rounded-lg shadow-md" />
      </div>
    </div>
  );
}

function CoverLetterNotFound() {
  return (
    <div 
      className="flex-1 flex flex-col items-center justify-center p-12 text-center"
      style={{ color: 'var(--text-primary)', background: 'var(--surface-0)' }}
    >
      <div className="max-w-md space-y-4">
        <h2 className="text-xl font-bold">Cover Letter Not Found</h2>
        <p className="text-sm text-[var(--text-secondary)]">
          The requested cover letter does not exist or has been deleted.
        </p>
        <div className="pt-2">
          <Link 
            to="/app/dashboard" 
            className="inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-semibold transition-all"
            style={{ background: 'var(--brand)', color: 'white' }}
          >
            Return to Dashboard
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function CoverLetterPage() {
  const { clId } = useParams();
  const {
    coverLetters = [],
    switchDocument,
    activeCoverLetter,
  } = useAppContext();

  const { activeTab, setActiveTab, theme, setMobileTab } = useWorkspaceContext();

  // Load cover letter data when URL changes
  useEffect(() => {
    if (clId) {
      const currentActiveId = activeCoverLetter?.id;
      if (String(currentActiveId) !== String(clId) || !activeCoverLetter) {
        // Find matching metadata
        const clItem = coverLetters.find(item => String(item.id) === String(clId));
        if (clItem) {
          switchDocument({ type: 'cover_letter', id: clId, document_data: clItem }, false);
        } else {
          // Just trigger basic load
          switchDocument({ type: 'cover_letter', id: clId }, false);
        }
      }
    }
  }, [clId, activeCoverLetter, coverLetters, switchDocument]);

  const exists = coverLetters.some(item => String(item.id) === String(clId));

  const isLibraryLoading = coverLetters.length === 0;
  const isDocumentLoading = exists && !activeCoverLetter;

  if (isLibraryLoading || isDocumentLoading) {
    return <CoverLetterSkeleton />;
  }

  if (!exists) {
    return <CoverLetterNotFound />;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, height: '100%', overflow: 'hidden', position: 'relative' }}>
      
      {/* Floating Glassmorphism Centered Navbar */}
      <div style={{
        position: 'absolute',
        top: 12,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 50,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        padding: '6px 14px',
        borderRadius: 20,
        background: theme === 'dark'
          ? 'rgba(15, 15, 22, 0.72)'
          : 'rgba(255, 255, 255, 0.72)',
        border: theme === 'dark'
          ? '1px solid rgba(255,255,255,0.07)'
          : '1px solid rgba(0,0,0,0.08)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        boxShadow: theme === 'dark'
          ? '0 4px 24px rgba(0,0,0,0.4), 0 1px 0 rgba(255,255,255,0.05) inset'
          : '0 4px 20px rgba(0,0,0,0.1), 0 1px 0 rgba(255,255,255,0.8) inset',
      }}>
        {[
          { id: 'chat',      label: 'AI',        icon: Bot },
          { id: 'editor',    label: 'Editor',    icon: PenLine },
          { id: 'templates', label: 'Templates', icon: LayoutGrid },
        ].map((tab) => {
          const active = activeTab === tab.id || (tab.id === 'chat' && activeTab === 'ai');
          const IconComp = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                if (typeof setMobileTab === 'function') {
                  setMobileTab(1);
                }
              }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 3,
                padding: '7px 13px',
                borderRadius: 14,
                border: 'none',
                cursor: 'pointer',
                outline: 'none',
                background: active
                  ? (theme === 'dark' ? 'rgba(59, 130, 246, 0.18)' : 'rgba(37, 99, 235, 0.1)')
                  : 'transparent',
                color: active
                  ? 'var(--brand)'
                  : (theme === 'dark' ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.4)'),
                transition: 'all 150ms ease',
                minWidth: 60,
              }}
              onMouseEnter={e => {
                if (!active) {
                  e.currentTarget.style.background = theme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)';
                  e.currentTarget.style.color = theme === 'dark' ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.7)';
                }
              }}
              onMouseLeave={e => {
                if (!active) {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = theme === 'dark' ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.4)';
                }
              }}
            >
              <IconComp size={15} strokeWidth={active ? 2.2 : 1.8} />
              <span style={{ fontSize: 9.5, fontWeight: active ? 700 : 500, letterSpacing: '0.02em', lineHeight: 1 }}>
                {tab.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* Main workspace panels */}
      <div style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {/* CenterWorkspace handles the 'templates' tab internally (renders
            TemplateGallery), exactly like the resume side — it was being
            excluded here, leaving the Templates tab with nothing to show. */}
        <CenterWorkspace />
        <RightWorkspace />
      </div>
    </div>
  );
}
